// Het sessiegeheugen van het portaal: waar de gebruiker stond en wat hij had
// gefilterd. Dat hoort bij de HUIDIGE werksessie en niet bij het account.
//
// Eén sessie = het portaal dat open blijft staan in de browser of de app. Zolang
// dat zo is, blijven pagina, subpagina en filters bewaard: een refresh of het
// heen-en-weer tussen modules brengt je terug waar je was. Een NIEUWE sessie
// begint met een schone lei: startpagina Market Prices, in elke module de eerste
// subpagina (bv. Samenvatting in Customer 360) en geen filters.
//
// Een nieuwe sessie is:
//   - een nieuw tabblad of venster (sessionStorage is dan leeg);
//   - een nieuwe aanmelding of een afmelding (AuthContext roept wisSessieGeheugen);
//   - een browser die het tabblad TERUGZET ("doorgaan waar je gebleven was",
//     tabbladherstel op een telefoon): die zet ook sessionStorage terug, dus
//     daarvoor kijken we naar het navigatietype en naar het laatste levensteken
//     (zie startSessie).
//
// Wie een filterstand écht wil bewaren, gebruikt de favorieten: die staan
// server-side en zijn een bewuste keuze.
//
// Thema, tabelkeuzes, huisstijl-voorgeheugen en de sessietoken blijven in
// localStorage: die verwarren niemand en horen bij het toestel.

import { clearSavedFilters } from './filterStorage';

// Sleutel van de actieve pagina en het patroon van de subpagina-sleutels
// (ordersTab, invoicesTab, c360Tab, …).
export const ACTIVE_PAGE_KEY = 'activePage';
const TAB_SLEUTEL = /Tab$/;

// Laatste levensteken van deze sessie (ms sinds epoch), in sessionStorage.
const LEVENSTEKEN_KEY = 'sessie.levensteken';
// Wordt het portaal opgestart terwijl het laatste levensteken ouder is dan dit,
// dan is het geen refresh meer maar een teruggezet tabblad van een vorige keer.
export const MAX_INACTIEF_MS = 60 * 60 * 1000;

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

function sleutels(store, test) {
  const out = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && test(k)) out.push(k);
    }
  } catch { /* private mode / storage geblokkeerd */ }
  return out;
}

// Eén tekstwaarde van de sessie lezen/schrijven (pagina, subpagina).
export function leesSessieWaarde(key) {
  try { return window.sessionStorage.getItem(key) || null; } catch { return null; }
}

export function schrijfSessieWaarde(key, waarde) {
  try {
    if (waarde == null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, String(waarde));
  } catch { /* ignore */ }
}

// Alle subpagina's waarop de gebruiker staat (voor de feedback-context).
export function actieveSubpaginas() {
  const uit = {};
  const store = sessionStore();
  if (!store) return uit;
  for (const k of sleutels(store, (s) => TAB_SLEUTEL.test(s))) {
    try { uit[k] = store.getItem(k); } catch { /* ignore */ }
  }
  return uit;
}

// Gooi het hele sessiegeheugen weg: filters, actieve pagina en subpagina's.
// localStorage zit erbij om de nalatenschap van de oude opzet op te ruimen, waar
// pagina en subpagina's nog per toestel bewaard werden.
export function wisSessieGeheugen() {
  clearSavedFilters();
  const stores = [];
  try { stores.push(window.sessionStorage); } catch { /* ignore */ }
  try { stores.push(window.localStorage); } catch { /* ignore */ }
  for (const store of stores) {
    for (const k of sleutels(store, (s) => s === ACTIVE_PAGE_KEY || TAB_SLEUTEL.test(s))) {
      try { store.removeItem(k); } catch { /* ignore */ }
    }
  }
}

function schrijfLevensteken(nu = Date.now()) {
  try { window.sessionStorage.setItem(LEVENSTEKEN_KEY, String(nu)); } catch { /* ignore */ }
}

// Navigatietype van deze pagina-lading. Een browser die een tabblad terugzet
// meldt dat als 'back_forward' (Chromium), een gewone refresh als 'reload'.
function navigatieType() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    return nav && nav.type ? nav.type : null;
  } catch { return null; }
}

// Zuivere beslissing, apart om te testen: is deze pagina-lading het begin van
// een nieuwe sessie? `levensteken` = wat er in sessionStorage stond (of null).
export function isNieuweSessie({ levensteken, navType, nu = Date.now(), maxInactief = MAX_INACTIEF_MS }) {
  if (levensteken == null) return true;                 // nieuw tabblad / venster
  const t = Number(levensteken);
  if (!Number.isFinite(t)) return true;                 // onleesbaar → schoon beginnen
  if (navType === 'back_forward') return true;          // teruggezet tabblad
  return nu - t > maxInactief;                          // te lang niets gehoord
}

// Bij het opstarten van de app aanroepen, VÓÓR React de eerste pagina kiest.
// Beslist of dit een nieuwe sessie is (en wist dan het sessiegeheugen) en houdt
// daarna het levensteken bij zolang het tabblad leeft.
export function startSessie() {
  const store = sessionStore();
  if (!store) return;
  let levensteken = null;
  try { levensteken = store.getItem(LEVENSTEKEN_KEY); } catch { /* ignore */ }
  if (isNieuweSessie({ levensteken, navType: navigatieType() })) wisSessieGeheugen();
  schrijfLevensteken();

  // Levensteken: elke minuut zolang het tabblad zichtbaar is, en op de momenten
  // die ertoe doen — terugkeren naar het tabblad en het verlaten van de pagina.
  // Zo is een refresh na een terugkeer altijd een voortzetting, en telt de tijd
  // dat het tabblad dicht was mee als afwezigheid.
  try {
    setInterval(() => { if (document.visibilityState !== 'hidden') schrijfLevensteken(); }, 60 * 1000);
    document.addEventListener('visibilitychange', () => schrijfLevensteken());
    window.addEventListener('pagehide', () => schrijfLevensteken());
    window.addEventListener('focus', () => schrijfLevensteken());
  } catch { /* ignore */ }
}
